// Password utility functions for secure password handling

/**
 * Hash a password using SHA-256
 * @param password - The plain text password to hash
 * @returns Promise<string> - The hashed password
 */
export const hashPassword = async (password: string): Promise<string> => {
	const encoder = new TextEncoder();
	const data = encoder.encode(password);
	const hashBuffer = await crypto.subtle.digest('SHA-256', data);
	const hashArray = Array.from(new Uint8Array(hashBuffer));
	const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
	return hashHex;
};

/**
 * Verify a password against a hash
 * @param password - The plain text password to verify
 * @param hash - The stored hash to compare against
 * @returns Promise<boolean> - True if password matches hash
 */
export const verifyPassword = async (password: string, hash: string): Promise<boolean> => {
	const passwordHash = await hashPassword(password);
	return passwordHash === hash;
};

/**
 * Check if a password meets minimum requirements
 * @param password - The password to validate
 * @returns boolean - True if password meets requirements
 */
export const validatePassword = (password: string): boolean => {
	// Minimum 4 characters for now, can be made more strict later
	return password.length >= 4;
};
